import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Continuous frozen-header report viewer. Renders the LIVE report body (all group
// members stacked) in ONE scroll container and freezes each portfolio's title band
// (.report-running-header) + any in-report control bar (.report-sticky-toolbar)
// + column names (<thead>) Excel-style via CSS `position: sticky` (see index.css,
// `.report-scroll` scope). Replaces the old paged.js clone viewer on screen;
// PRINT + xlsx export keep their own DOM trees.
//
// Why live DOM (not paged.js clones): (1) `transform: scale` — the old fit
// mechanism — breaks `position: sticky`; CSS `zoom` (used here for fit-to-width)
// reflows and preserves it. (2) Live nodes survive React re-renders, so row-expand
// updates in place with no re-pagination. Field-insight/hover stay DELEGATED on the
// scroll container (report cells carry no per-cell React handlers), retargeted from
// the old `.pagedjs_page` scoping to the live `.report-member` sections.
//
// Props:
//   children      - the report body (all members stacked; forced-light). Also the
//                   xlsx-export DOM, exposed via sourceRef.
//   sourceRef     - ref the parent reads for xlsx export (points at the live wrapper)
//   zoom          - user zoom multiplier on top of the auto fit-to-width (1 = fit)
//   alignLeft     - left-align the content (parent sets this while the field-insight
//                   panel is open) so the empty right strip is contiguous for it
//   reserveRight  - px of viewport width the fit must leave free on the right (panel)
//   onPageLayout  - called with {pageRightEdge, viewportWidth} when the content strip
//                   geometry changes, so the parent lays the panel out beside it
//   memberCount   - number of stacked .report-member sections (re-attaches observers)
//   onActiveMemberChange - called with the index of the member currently under the
//                   frozen header (scroll-spy), so the parent highlights its tab
//   scrollRequest - {idx, nonce}: bump nonce to scroll the viewport to that member
//   onBodyToggle  - (memberIdx, key) when an expandable row is (single-)clicked
//   onToggleAll   - (memberIdx, keys, expand) when an Expand/Collapse All control fires
//   onFieldSingleClick / onFieldDoubleClick - (cellCtx) when a data cell is single- /
//                   double-clicked; discrimination (e.detail + 250ms timer) lives here
const PAD = 16;               // px gutter between the viewport and the sheet
// Side margins INSIDE the white sheet, so report text never touches its border
// (defect 2026-08-17: the FI Holdings Par Value / YOC columns ran flush to the
// edge). On paper the paged.js page box already insets the report 0.5in per side;
// 20px is the screen counterpart - 5x the widest cell padding, and small enough
// that absorbing it into the fit costs under 4% of the rendered text size (a 1:1
// 48px match would cost ~8%).
//
// It is added OUTSIDE the measured content column, by widening the sheet, NOT as
// padding on the column: padding there would hand the tables a narrower box than
// the fit measured, and the widest families - whose intrinsic table is already
// wider than DESIGN_WIDTH - would overflow it and touch the edge again.
const SHEET_PAD_X = 20;
const DESIGN_WIDTH = 1056;    // landscape design width the reports are laid out at; also
                              // the MINIMUM content width - in-report controls (e.g. the
                              // yield-on-cost "Both" basis) can widen the measured content
// Default on-screen size = a full fit-to-width. Product decision (2026-08-06,
// superseding the 2026-07-15 inset): a generated report opens at 100% and FILLS
// the window it is generated in, rather than resting inset from it. This fraction
// lives in the FIT (not the user zoom multiplier), so the titlebar readout still
// shows 100% at rest and each zoom step stays a clean 10pt.
const FIT_FRACTION = 1;
const CLICK_DELAY = 250;      // ms to wait for a possible second click before firing single
// Upper bound on the tab-jump correction passes below. One pass lands it in
// practice; the bound only exists so a pathologically unstable layout (a member
// that resizes every frame) cannot spin forever.
const MAX_JUMP_CORRECTIONS = 12;

// Collapse runs of whitespace to single spaces (multi-line <th> headers span rows).
const collapseWs = (s) => String(s || '').replace(/\s+/g, ' ').trim();

// The member portfolio section an element belongs to, so section/header/attribution
// lookups stay scoped to the right portfolio in a stacked group report.
const memberSectionOf = (el) => (el && el.closest ? el.closest('.report-member') : null);
const memberIndexOf = (el) => {
  const sec = memberSectionOf(el);
  const idx = sec && sec.dataset ? Number(sec.dataset.memberIndex) : 0;
  return Number.isFinite(idx) ? idx : 0;
};

// Column header for a cell: the last thead row's cell at the same column index.
// Fail-soft to null (cells without a proper thead, colspans, etc.).
const columnLabelFor = (td) => {
  try {
    const table = td.closest('table');
    const theadRows = table ? table.querySelectorAll('thead tr') : null;
    if (!theadRows || theadRows.length === 0) return null;
    const headRow = theadRows[theadRows.length - 1];
    const cell = headRow.cells ? headRow.cells[td.cellIndex] : null;
    const label = cell ? collapseWs(cell.textContent) : '';
    return label || null;
  } catch (_e) {
    return null;
  }
};

// Row description: an explicit data-row-description on the row (rows whose
// name cell is intentionally blank, e.g. the APX-style class subtotal rows),
// else the cell under the column whose header contains 'Description', else the
// first cell in the row whose text contains letters. Fail-soft to null.
const rowDescriptionFor = (td) => {
  try {
    const row = td.closest('tr');
    if (!row || !row.cells) return null;
    const explicit = row.getAttribute && row.getAttribute('data-row-description');
    if (explicit) return collapseWs(explicit);
    const table = td.closest('table');
    const theadRows = table ? table.querySelectorAll('thead tr') : null;
    if (theadRows && theadRows.length > 0) {
      const headRow = theadRows[theadRows.length - 1];
      for (let i = 0; i < row.cells.length; i += 1) {
        const h = headRow.cells ? headRow.cells[i] : null;
        if (h && /description/i.test(h.textContent || '')) {
          const val = collapseWs(row.cells[i].textContent);
          if (val) return val;
        }
      }
    }
    for (let i = 0; i < row.cells.length; i += 1) {
      const val = collapseWs(row.cells[i].textContent);
      if (val && /[a-z]/i.test(val)) return val;
    }
    return null;
  } catch (_e) {
    return null;
  }
};

// Nearest h2/h3 heading that precedes the field in document order within the
// same member section. For a table cell the anchor is its table; a non-table
// field (data-insight-field) anchors on itself. Fail-soft to null.
const sectionFor = (el) => {
  try {
    const scope = memberSectionOf(el);
    if (!scope) return null;
    const anchor = el.closest('table') || el;
    const headings = scope.querySelectorAll('h2, h3');
    let found = null;
    for (let i = 0; i < headings.length; i += 1) {
      const h = headings[i];
      // heading precedes the anchor (FOLLOWING from the heading's view => before it)
      if (h.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_FOLLOWING) {
        found = h;
      }
    }
    return found ? collapseWs(found.textContent) : null;
  } catch (_e) {
    return null;
  }
};

// The inspectable field element for an event target, or null. A field is either
// a data cell (<td> inside a <table>) or an explicitly opted-in non-table field
// (data-insight-field="<label>" on the VALUE element - e.g. the Total/Market/
// Cash Value summary tiles, which render as <p> cards and would otherwise be
// dead to the delegated hover/click). Both must sit inside a member section and
// carry non-empty text.
//
// data-no-insight opts an element and its subtree OUT: the row-expand chevrons
// live INSIDE a <td>, so without this a click on the arrow both toggled the tax
// lots and opened the side chat. They are controls, not report figures. The
// mark is checked against the event target (not the resolved cell) so the rest
// of the same cell - the par figure the chevron sits beside - stays inspectable.
const fieldTargetOf = (target) => {
  if (!target.closest) return null;
  if (target.closest('[data-no-insight]')) return null;
  const el = target.closest('td, [data-insight-field]');
  if (!el || !memberSectionOf(el)) return null;
  if (el.tagName === 'TD' && !el.closest('table')) return null;
  return el.textContent && el.textContent.trim() ? el : null;
};

// Build the {section, column_label, row_description, field_label, cell_value,
// memberIndex} context for a field element. memberIndex follows the CLICKED
// portfolio (not scroll-spy state), so a click deep in member 2 attributes to
// member 2 even while member 1 is at the top. Marked non-table fields carry
// their label instead of the table column/row coordinates.
const buildCellCtx = (el) => {
  const isCell = el.tagName === 'TD';
  return {
    section: sectionFor(el),
    column_label: isCell ? columnLabelFor(el) : null,
    row_description: isCell ? rowDescriptionFor(el) : null,
    field_label: isCell ? null : (el.getAttribute('data-insight-field') || null),
    cell_value: el.textContent.trim(),
    memberIndex: memberIndexOf(el),
  };
};

// Write a CSS custom property only when the value would actually change.
//
// The band measurements below run inside a ResizeObserver callback and the
// properties they write drive `position: sticky` offsets, so an unconditional
// write dirties style on every pass and re-fires the observer, which measures
// and writes again: "ResizeObserver loop completed with undelivered
// notifications", which CRA surfaces as the red error overlay (seen when
// closing a report window). Skipping the no-op write makes a settled layout
// produce no mutation at all, which is what ends the loop.
const setVar = (el, name, value) => {
  if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value);
};

// Disconnect an observer the moment the window owning its targets starts
// tearing down (pagehide), instead of waiting for React's unmount cleanup.
//
// The viewer portals into a popup document while its observers live in the
// OPENER realm. When the popup dies (OS close, or the report E2E harness
// removing the iframe that stands in for one), the observed document detaches
// FIRST and the unmount cleanup only runs a task later. In that gap Chromium
// finds observations it can never deliver again and reports "ResizeObserver
// loop completed with undelivered notifications" on the opener - the CRA red
// overlay, one error event per still-connected observer (the iframe teardown
// hit it every time in headless Chrome; a real OS popup close happened to stay
// clean, but only because the whole frame tree dies at once). pagehide fires
// synchronously for both teardowns, before the document is gone. Returns the
// effect cleanup.
const disconnectOnPagehide = (ro, el) => {
  const win = el.ownerDocument ? el.ownerDocument.defaultView : null;
  const halt = () => ro.disconnect();
  if (win) win.addEventListener('pagehide', halt);
  return () => {
    if (win) { try { win.removeEventListener('pagehide', halt); } catch (e) { /* closed popup */ } }
    ro.disconnect();
  };
};

const ContinuousReportViewer = ({
  children,
  sourceRef: sourceRefProp,
  zoom = 1,
  alignLeft = false,
  reserveRight = 0,
  onPageLayout,
  memberCount = 1,
  onActiveMemberChange,
  scrollRequest,
  onBodyToggle,
  onToggleAll,
  onFieldSingleClick,
  onFieldDoubleClick,
}) => {
  const localSourceRef = useRef(null);
  const sourceRef = sourceRefProp || localSourceRef;
  const viewportRef = useRef(null);
  // Pending single-click timer: a first click schedules the single action; a second
  // click within CLICK_DELAY cancels it and runs the double action instead.
  const clickTimerRef = useRef(null);

  const [fitZoom, setFitZoom] = useState(1);
  // Intrinsic (unzoomed) content width in CSS px. DESIGN_WIDTH for reports laid
  // out to the design page; wider when in-report controls (e.g. the yield-on-cost
  // "Both" basis) add columns whose table overflows it. The content box and the
  // fit both track it so columns never run off the page at rest.
  const [contentWidth, setContentWidth] = useState(DESIGN_WIDTH);
  const effectiveZoom = fitZoom * zoom;
  // Read the live effective zoom inside observers (which must not re-subscribe on
  // every zoom change) so band measurements convert rendered px -> CSS px correctly.
  const effectiveZoomRef = useRef(effectiveZoom);
  effectiveZoomRef.current = effectiveZoom;

  // --- fit-to-width (CSS zoom, NOT transform: scale, which breaks sticky) --------
  const reserveRightRef = useRef(reserveRight);
  reserveRightRef.current = reserveRight;
  const recomputeFit = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    // Intrinsic content width: lay the content out at the natural DESIGN_WIDTH
    // and read how far it overflows (restored before paint - this runs inside
    // layout effects / observer callbacks). scrollWidth and clientWidth share
    // the element's (zoom-scaled) coordinate space, so their ratio is
    // zoom-invariant - refitting never changes the measured width (no
    // fit -> width -> fit feedback loop).
    let contentW = DESIGN_WIDTH;
    const content = sourceRef.current;
    if (content) {
      const prevWidth = content.style.width;
      content.style.width = `${DESIGN_WIDTH}px`;
      if (content.clientWidth > 0) {
        // Deep scan: the member bands use content-visibility, whose containment
        // stops a wide table's layout overflow from propagating into the
        // content element's own scrollWidth (verified live in Chrome - the
        // sheet read 1056 while its table was 1085). Take the max across the
        // tables and their wrappers; offscreen (layout-skipped) bands measure 0
        // and are refit by the band ResizeObserver when they paint in.
        let maxSW = content.scrollWidth;
        content.querySelectorAll('table').forEach((t) => {
          maxSW = Math.max(maxSW, t.scrollWidth);
          if (t.parentElement) maxSW = Math.max(maxSW, t.parentElement.scrollWidth);
        });
        contentW = Math.max(
          DESIGN_WIDTH,
          Math.ceil(DESIGN_WIDTH * (maxSW / content.clientWidth)),
        );
      }
      content.style.width = prevWidth;
    }
    setContentWidth(contentW);
    // Yield the reserved right strip (field-insight panel), capped at half the
    // viewport so a small window still shows a readable report.
    const reserve = Math.min(reserveRightRef.current || 0, Math.max(0, vp.clientWidth - 2 * PAD) / 2);
    const availW = vp.clientWidth - 2 * PAD - reserve;
    if (availW <= 0) return;
    // Fit the SHEET, gutters included - otherwise the sheet would sit 2 gutters
    // wider than the window and scroll horizontally at rest.
    setFitZoom(Math.max(0.1, (availW / (contentW + 2 * SHEET_PAD_X)) * FIT_FRACTION));
  }, [sourceRef]);

  // --- content-strip geometry reporting (drives the field-insight panel layout) --
  const layoutInputsRef = useRef({ effectiveZoom, alignLeft, contentWidth });
  layoutInputsRef.current = { effectiveZoom, alignLeft, contentWidth };
  const onPageLayoutRef = useRef(onPageLayout);
  useEffect(() => { onPageLayoutRef.current = onPageLayout; });
  const reportLayout = useCallback(() => {
    const cb = onPageLayoutRef.current;
    const vp = viewportRef.current;
    if (!cb || !vp) return;
    const { effectiveZoom: z, alignLeft: left, contentWidth: cw } = layoutInputsRef.current;
    const viewportWidth = vp.clientWidth;
    // The panel is laid out beside the SHEET, so its right edge includes the gutters.
    const sheetW = (cw + 2 * SHEET_PAD_X) * z;
    const leftEdge = left ? PAD : Math.max(PAD, (viewportWidth - sheetW) / 2);
    cb({ pageRightEdge: leftEdge + sheetW, viewportWidth });
  }, []);

  // --- member hit-testability: we decide content-visibility relevance ------------
  // `.report-member` carries `content-visibility: auto` (index.css) for
  // progressive rendering, but Chromium's own relevance bookkeeping can go stale
  // for windows whose DOM mounts with report data already in hand (the chat
  // handoff cached/auto-open paths): the section stays laid out and painted yet
  // its subtree is SKIPPED FOR HIT-TESTING, so every mouse event targets the
  // section and the delegated field-insight hover/click below never sees a <td>.
  // Members within 1.5 viewport-heights of the scrollport are stamped
  // `data-cv-near` (-> content-visibility: visible, always hit-testable); far
  // members keep `auto`. Geometry comes from getBoundingClientRect, which stays
  // correct even when the browser's relevance state is stale.
  const updateMemberVisibility = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const vpRect = vp.getBoundingClientRect();
    const margin = 1.5 * vp.clientHeight;
    vp.querySelectorAll('.report-member').forEach((sec) => {
      const r = sec.getBoundingClientRect();
      const near = r.bottom >= vpRect.top - margin && r.top <= vpRect.bottom + margin;
      if (near !== sec.hasAttribute('data-cv-near')) {
        if (near) sec.setAttribute('data-cv-near', '');
        else sec.removeAttribute('data-cv-near');
      }
    });
  }, []);
  // Before first paint (and on zoom/member changes) so a freshly opened window's
  // cells are clickable immediately.
  useLayoutEffect(() => { updateMemberVisibility(); }, [updateMemberVisibility, memberCount, effectiveZoom]);

  useLayoutEffect(() => { recomputeFit(); }, [recomputeFit, reserveRight]);
  useLayoutEffect(() => { reportLayout(); }, [reportLayout, effectiveZoom, alignLeft, contentWidth]);
  useEffect(() => {
    const vp = viewportRef.current;
    if (vp && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => { recomputeFit(); reportLayout(); updateMemberVisibility(); });
      ro.observe(vp);
      return disconnectOnPagehide(ro, vp);
    }
    const onResize = () => { recomputeFit(); reportLayout(); updateMemberVisibility(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeFit, reportLayout, updateMemberVisibility]);
  // --- per-member band-height measurement (the sticky <thead> `top` offset) -------
  // Each member's title band is its own height (firm/title/account/date/notice/logo),
  // so `--band-h` (and `--hdr-row1-h` for the one two-tier-thead report) is measured
  // per section and written on that .report-member. Measured in CSS px
  // (rect.height / effectiveZoom) so it stays valid across zoom without re-measuring.
  //
  // In-report control bars (.report-sticky-toolbar) are part of the same frozen
  // band, so their height feeds it too - see the toolbar pass below.
  const toolbarHostsRef = useRef([]);
  const measureBands = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const z = effectiveZoomRef.current || 1;
    // Control bars: the bar pins under the title band and the column names pin
    // under the bar, so the bar's height is written as `--toolbar-h` on its
    // PARENT - the block that also holds the table. Scoped there rather than on
    // the member because a member can stack several tables and only the one in
    // that block sits under the bar; every other thead keeps `--toolbar-h`
    // unset (i.e. the 0px default). Hosts are tracked so a report that loses
    // its controls cannot strand a stale offset - but ONLY the hosts that lost
    // their bar are cleared. Blanket-clearing every tracked host and re-setting
    // it rewrote an identical value on every pass, which is exactly the style
    // mutation that fed the ResizeObserver loop (see setVar).
    const hosts = [];
    vp.querySelectorAll('.report-sticky-toolbar').forEach((bar) => {
      const host = bar.parentElement;
      if (!host) return;
      setVar(host, '--toolbar-h', `${Math.round(bar.getBoundingClientRect().height / z)}px`);
      hosts.push(host);
    });
    toolbarHostsRef.current.forEach((host) => {
      if (!hosts.includes(host)) host.style.removeProperty('--toolbar-h');
    });
    toolbarHostsRef.current = hosts;
    vp.querySelectorAll('.report-member').forEach((section) => {
      const band = section.querySelector(':scope > .report-body > .report-running-header')
        || section.querySelector('.report-running-header');
      const bandH = band ? band.getBoundingClientRect().height / z : 0;
      setVar(section, '--band-h', `${Math.round(bandH)}px`);
      // Two-tier grouped header: measure the FIRST header row so the second row pins
      // below it. Absent (single-row theads) -> 0, i.e. inert.
      const firstHeadRow = section.querySelector('thead tr');
      const twoTier = firstHeadRow && firstHeadRow.parentElement
        && firstHeadRow.parentElement.querySelectorAll('tr').length > 1;
      const row1H = twoTier ? firstHeadRow.getBoundingClientRect().height / z : 0;
      setVar(section, '--hdr-row1-h', `${Math.round(row1H)}px`);
    });
  }, []);

  useLayoutEffect(() => { measureBands(); }, [measureBands, memberCount, effectiveZoom]);
  // Re-measure when a band resizes (e.g. the firm-logo toggle grows it).
  // recomputeFit also re-runs here: a member painting in via content-visibility
  // resizes its band from 0, and its tables only count toward the intrinsic
  // content width once laid out.
  // The control bars are observed alongside the bands: narrowing the window
  // wraps their controls onto a second line, which moves the column names.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => { measureBands(); recomputeFit(); });
    vp.querySelectorAll('.report-member .report-running-header, .report-member .report-sticky-toolbar')
      .forEach((el) => ro.observe(el));
    return disconnectOnPagehide(ro, vp);
  }, [measureBands, recomputeFit, memberCount]);

  // Refit + re-measure when the report content itself changes shape: in-report
  // controls (e.g. the yield-on-cost basis dropdown) add/remove columns and
  // change the intrinsic width, and a control bar can appear or disappear with
  // the payload (which the memberCount-keyed observer above would not catch).
  // childList-only, so the style writes recomputeFit/measureBands make and the
  // data-cv-near stamps updateMemberVisibility writes - all attribute
  // mutations - can never re-trigger it.
  useEffect(() => {
    const content = sourceRef.current;
    if (!content || typeof MutationObserver === 'undefined') return undefined;
    const mo = new MutationObserver(() => {
      recomputeFit(); reportLayout(); updateMemberVisibility(); measureBands();
    });
    mo.observe(content, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [sourceRef, recomputeFit, reportLayout, updateMemberVisibility, measureBands]);

  // --- scroll-spy: which member is under the frozen header (tab highlight) --------
  const activeMemberRef = useRef(-1);
  const rafRef = useRef(0);
  const onScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      const vp = viewportRef.current;
      if (!vp) return;
      updateMemberVisibility();
      if (!onActiveMemberChange) return;
      const vpTop = vp.getBoundingClientRect().top;
      const sections = vp.querySelectorAll('.report-member');
      let active = 0;
      sections.forEach((sec) => {
        // The last member whose top has reached (crossed above) the viewport top is
        // the one currently occupying the frozen band.
        if (sec.getBoundingClientRect().top - vpTop <= 4) {
          const idx = Number(sec.dataset.memberIndex);
          if (Number.isFinite(idx)) active = idx;
        }
      });
      if (active !== activeMemberRef.current) {
        activeMemberRef.current = active;
        onActiveMemberChange(active);
      }
    });
  }, [onActiveMemberChange, updateMemberVisibility]);
  useEffect(() => () => { if (rafRef.current) window.cancelAnimationFrame(rafRef.current); }, []);

  // --- tab click -> scroll to that member ----------------------------------------
  // The destination CANNOT be computed up front, which is what a single absolute
  // scrollTo assumed. Members carry `content-visibility: auto` with a 900px
  // `contain-intrinsic-size` placeholder (index.css), so every member that has
  // not painted yet is only ESTIMATED. Aiming at that estimate lands wrong by the
  // sum of the estimate errors between here and the target: measured at +5537px
  // for a 25-member jump, exactly the document's height drift as those members
  // painted in. That is why a neighbouring tab was always right and a distant one
  // was not, and why it undershot for members taller than the placeholder and
  // overshot for shorter ones.
  //
  // So jump, re-measure, correct, until the member really is at the top. Each
  // pass paints the members around the new position - the only thing that moves
  // the ground - and the next pass corrects for it.
  //
  // The jump is INSTANT, not smooth, and that is load-bearing rather than a taste
  // call: a smooth scroll traverses the whole document and paints every member on
  // the way past, which is both what creates the drift and a full render of
  // members the PM never asked to see. Jumping straight there paints only the
  // destination's neighbours, and the correction settles in ONE pass.
  const scrollNonceRef = useRef(scrollRequest ? scrollRequest.nonce : null);
  useEffect(() => {
    if (!scrollRequest || scrollRequest.nonce === scrollNonceRef.current) return undefined;
    scrollNonceRef.current = scrollRequest.nonce;
    const vp = viewportRef.current;
    if (!vp) return undefined;
    let raf = null;
    let passes = 0;
    let stalls = 0;
    const step = () => {
      raf = null;
      const sec = vp.querySelector(`.report-member[data-member-index="${scrollRequest.idx}"]`);
      if (!sec) return;
      const delta = sec.getBoundingClientRect().top - vp.getBoundingClientRect().top;
      if (Math.abs(delta) <= 1 || passes >= MAX_JUMP_CORRECTIONS) return;
      passes += 1;
      const before = vp.scrollTop;
      vp.scrollTop += delta;
      // Paint what is now near the viewport before measuring again, so the next
      // pass sees real heights rather than the placeholder it just scrolled past.
      updateMemberVisibility();
      // No movement means scrollTop is clamped at the end of the document - but
      // that is usually TEMPORARY on a long jump, because the document is
      // estimated SHORTER than it really is, and the members that just painted
      // extend it. Giving up on the first stall would stop the jump short every
      // time. Two stalls in a row means the member genuinely cannot reach the
      // top: it is the last one and shorter than the viewport, so nothing
      // further will paint and the budget would just spin.
      stalls = vp.scrollTop === before ? stalls + 1 : 0;
      if (stalls >= 2) return;
      raf = window.requestAnimationFrame(step);
    };
    step();
    return () => { if (raf !== null) window.cancelAnimationFrame(raf); };
  }, [scrollRequest, updateMemberVisibility]);

  // --- delegated click: row expand + field-insight single/double -----------------
  const onViewportClick = useCallback((e) => {
    if (!e.target.closest) return;
    // Expand/Collapse All wins outright: never treat it as a field click.
    const all = e.target.closest('[data-toggle-all]');
    if (all) {
      if (onToggleAll) {
        const keys = String(all.getAttribute('data-toggle-all-keys') || '').split(',').filter(Boolean);
        onToggleAll(memberIndexOf(all), keys, all.getAttribute('data-toggle-all') === 'expand');
      }
      return;
    }
    // Row expand key for the clicked row, if any. Deferred into the single-vs-double
    // discrimination below (a double-click resolves to "explain" and skips the
    // toggle, so double-clicking a cell to explain never flickers the lot rows).
    const toggleHit = onBodyToggle ? e.target.closest('[data-toggle-key]') : null;
    const toggleKey = toggleHit ? toggleHit.getAttribute('data-toggle-key') : null;
    const fireToggle = () => {
      if (onBodyToggle && toggleKey != null) onBodyToggle(memberIndexOf(toggleHit), toggleKey);
    };

    // Field click: a table data cell or a marked non-table field (see fieldTargetOf).
    const fieldEl = (onFieldSingleClick || onFieldDoubleClick) ? fieldTargetOf(e.target) : null;

    // Not a field: the toggle is the only action, so run it now.
    if (!fieldEl) {
      fireToggle();
      return;
    }

    // Only e.detail === 2 is a double-click; 3+ must not re-fire the explanation.
    if (e.detail === 2) {
      if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
      const sel = window.getSelection && window.getSelection();
      if (sel) sel.removeAllRanges();
      if (onFieldDoubleClick) onFieldDoubleClick(buildCellCtx(fieldEl));
      return;
    }
    if (e.detail > 2) return;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    const ctx = buildCellCtx(fieldEl);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      // Resolved single-click: toggle the row (deferred from click 1) and open insight.
      fireToggle();
      if (onFieldSingleClick) onFieldSingleClick(ctx);
    }, CLICK_DELAY);
  }, [onBodyToggle, onToggleAll, onFieldSingleClick, onFieldDoubleClick]);

  // Hover affordance: underline the inspectable data cell under the cursor.
  const hoveredTdRef = useRef(null);
  const clearHoverUnderline = useCallback(() => {
    const el = hoveredTdRef.current;
    if (el) {
      el.style.textDecoration = '';
      el.style.cursor = '';
      hoveredTdRef.current = null;
    }
  }, []);
  const onViewportMouseOver = useCallback((e) => {
    if (!(onFieldSingleClick || onFieldDoubleClick)) return;
    const fieldEl = fieldTargetOf(e.target);
    if (fieldEl) {
      if (hoveredTdRef.current !== fieldEl) {
        clearHoverUnderline();
        fieldEl.style.textDecoration = 'underline';
        fieldEl.style.cursor = 'pointer';
        hoveredTdRef.current = fieldEl;
      }
    } else if (hoveredTdRef.current) {
      clearHoverUnderline();
    }
  }, [onFieldSingleClick, onFieldDoubleClick, clearHoverUnderline]);

  // Abandon any pending single-click resolution on unmount (its closure holds a cell
  // context that a teardown would otherwise fire against a detached node).
  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);

  return (
    <div className="flex h-full w-full flex-col bg-gray-100">
      <div
        ref={viewportRef}
        className="report-scroll relative flex-1 overflow-auto"
        onScroll={onScroll}
        onClick={onViewportClick}
        onMouseOver={onViewportMouseOver}
        onMouseLeave={clearHoverUnderline}
      >
        {/* min-w-full + w-max: at/under fit the column is exactly the viewport width so
            justify-center centers it; a zoomed-in (wider) column grows past the
            viewport and scrolls horizontally without clipping its left edge. */}
        <div
          className={`flex ${alignLeft ? 'justify-start' : 'justify-center'} w-max min-w-full`}
          style={{ padding: PAD }}
        >
          {/* The sheet owns the paper: its colour, its shadow, the fit `zoom` and the
              side margins. The content column inside it is exactly the width the fit
              measured, so the margins are extra sheet rather than a squeezed report -
              and because they sit on a shared ancestor, the frozen title band, the
              sticky control bar and the column headers all shift with their tables.
              The column keeps the ref: it is the xlsx export's DOM, which must stay
              the report and nothing else. */}
          <div
            className="report-sheet bg-white text-black shadow-lg"
            style={{
              width: contentWidth + 2 * SHEET_PAD_X,
              padding: `0 ${SHEET_PAD_X}px`,
              zoom: effectiveZoom,
            }}
          >
            <div ref={sourceRef} className="report-scroll-content" style={{ width: contentWidth }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContinuousReportViewer;
